import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import Header from "../header/header";
import NavBar from "../nav/nav-bar";
import Footer from "../footer/footer";
import detail from "../assets/images/detail.svg";
import Service from "../webservice/http";
import ClipLoader from "react-spinners/ClipLoader";
import { NotificationContainer } from "react-notifications";

const getVersionColor = (version) => {
  if (!version || version === "Original") return "";
  const versionNum =
    typeof version === "number"
      ? version
      : parseInt(version.toString().match(/\d+/)?.[0] || 0);

  const fixedColors = [
    "hsla(180, 95%, 75%, 0.9)", // Cyan (180° - blue-green)
    "hsla(320, 90%, 80%, 0.9)", // Magenta (320° - pink-purple)
    "hsla(30, 100%, 75%, 0.9)", // Orange (30° - red-orange)
    "hsla(90, 85%, 70%, 0.9)", // Lime (90° - yellow-green)
    "hsla(350, 95%, 80%, 0.9)", // Rose (350° - pink-red)
    "hsla(45, 100%, 70%, 0.9)", // Gold (45° - yellow-orange)
    "hsla(260, 90%, 80%, 0.9)", // Violet (260° - blue-purple)
    "hsla(160, 85%, 75%, 0.9)", // Mint (160° - green-cyan)
    "hsla(15, 95%, 75%, 0.9)", // Coral (15° - red)
    "hsla(280, 85%, 82%, 0.9)", // Lavender (280° - purple)
  ];

  if (versionNum >= 1 && versionNum <= 10) {
    return fixedColors[versionNum - 1];
  }

  return `hsla(${(versionNum * 137.5) % 360}, 90%, 82%, 0.9)`;
};

const simpleDiff = (oldStr, newStr) => {
  if (!oldStr) return [{ value: newStr, added: true }];
  if (!newStr) return [{ value: oldStr, removed: true }];

  const oldParts = oldStr.split(/(\b)/).filter((p) => p.length > 0);
  const newParts = newStr.split(/(\b)/).filter((p) => p.length > 0);

  let oldIdx = 0;
  let newIdx = 0;
  const result = [];

  while (oldIdx < oldParts.length || newIdx < newParts.length) {
    if (
      oldIdx < oldParts.length &&
      newIdx < newParts.length &&
      oldParts[oldIdx] === newParts[newIdx]
    ) {
      result.push({ value: oldParts[oldIdx], equal: true });
      oldIdx++;
      newIdx++;
    } else {
      let bestMatch = null;
      const limit = 20;

      for (let i = 0; i < limit && oldIdx + i < oldParts.length; i++) {
        for (let j = 0; j < limit && newIdx + j < newParts.length; j++) {
          if (i === 0 && j === 0) continue;
          if (oldParts[oldIdx + i] === newParts[newIdx + j]) {
            const val = oldParts[oldIdx + i];

            const isWord = /[a-zA-Z0-9]{2,}/.test(val);
            const score = isWord ? 10 : 1;
            const distance = i + j;
            const finalScore = score * 10 - distance;

            if (!bestMatch || finalScore > bestMatch.score) {
              bestMatch = { i, j, score: finalScore };
            }
          }
        }
      }

      if (bestMatch && bestMatch.score > 0) {
        const removed = oldParts.slice(oldIdx, oldIdx + bestMatch.i).join("");
        const added = newParts.slice(newIdx, newIdx + bestMatch.j).join("");

        if (removed) result.push({ value: removed, removed: true });
        if (added) result.push({ value: added, added: true });

        oldIdx += bestMatch.i;
        newIdx += bestMatch.j;
      } else {
        if (oldIdx < oldParts.length) {
          result.push({ value: oldParts[oldIdx], removed: true });
          oldIdx++;
        }
        if (newIdx < newParts.length) {
          result.push({ value: newParts[newIdx], added: true });
          newIdx++;
        }
      }
    }
  }

  return result;
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    const dateStr = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${dateStr} ${timeStr}`;
  } catch (e) {
    return isoString.split("T")[0];
  }
};

const formatTime = (sec) => {
  if (typeof sec !== "number" || isNaN(sec)) return "-";
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

function TranscriptionAudit() {
  const { callID } = useParams();
  const history = useHistory();
  const location = useLocation();
  const service = useMemo(() => new Service(), []);
  const [loading, setLoading] = useState(true);
  const [callData, setCallData] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [hasRevisionsInBothLanguages, setHasRevisionsInBothLanguages] =
    useState(false);

  const [displayEnglish, setDisplayEnglish] = useState(true);
  const [isLanguageEnglish, setIsLanguageEnglish] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [notesData, setNotesData] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesInput, setNotesInput] = useState("");
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await service
          .get(`api/call/${callID}/call_insight/`)
          .catch(() => null);

        if (response) {
          setCallData(response);

          const langIsEnglish = response.language === "English";
          setIsLanguageEnglish(langIsEnglish);

          // Check if both languages have revised versions
          const hasEnglishRevisions =
            (response.edit_history?.edited_serialized_transcription || [])
              .length > 0;
          const hasOriginalLangRevisions =
            (response.edit_history?.edited_early_sz_transcription || [])
              .length > 0;

          // Enable toggle only if BOTH languages have revisions
          setHasRevisionsInBothLanguages(
            hasEnglishRevisions && hasOriginalLangRevisions,
          );

          // Smart default: Only on initial load, choose the language
          if (!initialLoadComplete) {
            // Check if displayEnglish was passed from Call Insight page
            if (location.state?.displayEnglish !== undefined) {
              // Respect the language toggle state from Call Insight page
              setDisplayEnglish(location.state.displayEnglish);
            } else {
              // Coming from Reports page - use smart defaults
              // Priority: If original language has revisions, default to original language
              if (hasOriginalLangRevisions && !langIsEnglish) {
                setDisplayEnglish(false);
              } else if (hasEnglishRevisions) {
                setDisplayEnglish(true);
              } else {
                setDisplayEnglish(true);
              }
            }
            setInitialLoadComplete(true);
          }

          const editHistory = displayEnglish
            ? response.edit_history?.edited_serialized_transcription || []
            : response.edit_history?.edited_early_sz_transcription || [];

          const versions = [
            {
              label: "Original",
              isOriginal: true,
              speech: response.speech,
              color: null,
            },
            ...editHistory
              .map((h) => ({
                ...h,
                label: `Revised Report ${h.version_number}.0`,
                color: getVersionColor(h.version_number),
                isOriginal: false,
              }))
              .sort((a, b) => a.version_number - b.version_number),
          ];

          setAllVersions(versions);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [callID, service, displayEnglish, initialLoadComplete]);

  const fetchNotes = React.useCallback(() => {
    if (!callID) return;
    service
      .get(`api/call/${callID}/edit_transcription/`)
      .then((res) => {
        const comment = res?.transcription_review_comment;
        if (comment) {
          const rawDate = res?.transcription_review_date || res?.edited_at || res?.updated_at || res?.created_at;
          const formattedDate = rawDate ? formatDateTime(rawDate) : "";
          setNotesData({
            notes: [{ id: "review", text: comment, date: formattedDate }],
          });
        } else {
          setNotesData({ notes: [] });
        }
      })
      .catch((err) => {
        console.error("Failed to fetch transcription review comment:", err);
      });
  }, [callID, service]);

  const handleAddNote = () => {
    const trimmed = notesInput.trim();
    if (!trimmed) return;

    service
      .post(`api/call/${callID}/edit_transcription/`, {
        transcription_review_comment: trimmed,
      })
      .then(() => {
        setNotesInput("");
        setShowAddNoteForm(false);
        setShowNotesModal(false);
        fetchNotes();
      })
      .catch((err) =>
        console.error("Failed to post transcription review comment:", err)
      );
  };

  const confirmDeleteNote = () => {
    service
      .post(`api/call/${callID}/edit_transcription/`, {
        transcription_review_comment: "",
      })
      .then(() => {
        setNoteToDelete(null);
        fetchNotes();
      })
      .catch((err) =>
        console.error("Failed to delete transcription review comment:", err)
      );
  };

  useEffect(() => {
    if (callID) fetchNotes();
  }, [callID, fetchNotes]);

  const renderDiff = (index, versionData) => {
    const originalItem = callData?.speech?.[index];
    if (!originalItem) return "";

    const change = versionData?.changes?.find((c) => c.index === index);
    if (!change) {
      const versionIndex = allVersions.findIndex(
        (v) => v.version_number === versionData.version_number,
      );

      if (versionIndex > 0) {
        for (let i = versionIndex - 1; i >= 0; i--) {
          const prevVersion = allVersions[i];
          if (prevVersion.isOriginal) {
            return displayEnglish
              ? originalItem.dialogue || ""
              : originalItem.early_dialogue || "";
          }
          const prevChange = prevVersion.changes?.find(
            (c) => c.index === index,
          );
          if (prevChange) {
            return prevChange.new_dialogue || "";
          }
        }
      }

      return displayEnglish
        ? originalItem.dialogue || ""
        : originalItem.early_dialogue || "";
    }

    const versionIndex = allVersions.findIndex(
      (v) => v.version_number === versionData.version_number,
    );

    let previousText = displayEnglish
      ? originalItem.dialogue || ""
      : originalItem.early_dialogue || "";

    if (versionIndex > 0) {
      for (let i = versionIndex - 1; i >= 0; i--) {
        const prevVersion = allVersions[i];
        if (prevVersion.isOriginal) {
          previousText = displayEnglish
            ? originalItem.dialogue || ""
            : originalItem.early_dialogue || "";
          break;
        }
        const prevChange = prevVersion.changes?.find((c) => c.index === index);
        if (prevChange) {
          previousText = prevChange.new_dialogue || "";
          break;
        }
      }
    }

    const newText = change.new_dialogue || "";
    const diff = simpleDiff(previousText, newText);

    return diff.map((part, i) => {
      if (part.removed) {
        return (
          <span
            key={i}
            style={{
              textDecoration: "line-through",
              color: "#991b1b",
              backgroundColor: "#fee2e2",
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {part.value}
          </span>
        );
      }
      if (part.added) {
        return (
          <span
            key={i}
            style={{
              backgroundColor: versionData.color || "#eff6ff",
              color: "#1f2937",
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {part.value}
          </span>
        );
      }
      return part.value;
    });
  };

  const renderAuditTrailDiff = (index) => {
    const originalItem = callData?.speech?.[index];
    if (!originalItem) return "";

    let currentText = displayEnglish
      ? originalItem.dialogue || ""
      : originalItem.early_dialogue || "";
    const allChanges = [];

    allVersions.forEach((version, vIdx) => {
      if (version.isOriginal) return;

      const change = version.changes?.find((c) => c.index === index);
      if (change) {
        allChanges.push({
          oldText: currentText,
          newText: change.new_dialogue,
          color: version.color,
          version: version.label,
        });
        currentText = change.new_dialogue;
      }
    });

    if (allChanges.length === 0) {
      return displayEnglish
        ? originalItem.dialogue || originalItem.early_dialogue
        : originalItem.early_dialogue || originalItem.dialogue;
    }

    const finalChange = allChanges[allChanges.length - 1];
    const diff = simpleDiff(
      displayEnglish
        ? originalItem.dialogue || originalItem.early_dialogue || ""
        : originalItem.early_dialogue || originalItem.dialogue || "",
      finalChange.newText,
    );

    return diff.map((part, i) => {
      if (part.removed) {
        return (
          <span
            key={i}
            style={{
              textDecoration: "line-through",
              color: "#991b1b",
              backgroundColor: "#fee2e2",
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {part.value}
          </span>
        );
      }
      if (part.added) {
        return (
          <span
            key={i}
            style={{
              backgroundColor: "hsla(260, 95%, 88%, 0.9)",
              color: "#1f2937",
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {part.value}
          </span>
        );
      }
      return part.value;
    });
  };

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />
      <NotificationContainer />
      <section className="custom-wrapper-glife">
        <div className="container-fluid">
          {loading ? (
            <div className="empty-call" style={{ height: "500px" }}>
              <ClipLoader color="#2056FF" size="50px" />
            </div>
          ) : (
            <>
              <div className="my-call">
                <div className="my-calls-column">
                  <div className="calls-top-pannel">
                    <div className="row">
                      <div className="col-lg-12 ">
                        <div className="pannel-heading clearfix div-padding">
                          <div className="pannel-heading-icon">
                            <img src={detail} alt="detail" />
                          </div>
                          <div className="pannel-heading-info">
                            <p>Transcription Audit Trail</p>
                            <h3 className="detail-heading">
                              Ref:{" "}
                              <span className="detail-heading-span">
                                {callData?.call_reference || callID}
                              </span>
                            </h3>
                          </div>
                          <div className="pull-right">
                            <button
                              onClick={() =>
                                history.push(`/call/${callID}/call-insight`)
                              }
                              className="btn btn-info pull-right"
                            >
                              Review
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!isLanguageEnglish && (
                <div style={{ marginBottom: "16px" }}>
                  <div className="transcription-header clearfix">
                    <div
                      className="toggle-switch"
                      style={{
                        opacity: hasRevisionsInBothLanguages ? 1 : 0.5,
                        cursor: hasRevisionsInBothLanguages
                          ? "pointer"
                          : "not-allowed",
                        pointerEvents: hasRevisionsInBothLanguages
                          ? "auto"
                          : "none",
                      }}
                    >
                      <label>
                        {displayEnglish && "English"}
                        {!displayEnglish && callData?.language}
                      </label>
                      <div className="toggle-switch-intregrate">
                        <input
                          type="checkbox"
                          id="global-lang-switch"
                          checked={displayEnglish}
                          onChange={() => setDisplayEnglish(!displayEnglish)}
                          disabled={!hasRevisionsInBothLanguages}
                        />
                        <label htmlFor="global-lang-switch"></label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div
                style={{
                  marginTop: "20px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingBottom: "20px",
                  borderBottom: "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    width: "auto",
                    paddingRight: "1rem",
                  }}
                >
                  {allVersions.map((version, vIdx) => (
                    <div
                      key={vIdx}
                      style={{
                        minWidth: allVersions.length === 1 ? "auto" : "580px",
                        flex:
                          allVersions.length === 1 ? "0 0 75%" : "0 0 580px",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "1rem",
                          borderBottom: "1px solid #f3f4f6",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "800",
                            fontSize: "16px",
                            marginBottom: "8px",
                            color: "#1f2937",
                            textAlign: "center",
                          }}
                        >
                          {version.label}
                        </div>

                        {version.isOriginal ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                              backgroundColor: "#f3f4f6",
                              padding: "8px",
                              borderRadius: "6px",
                              textAlign: "center",
                              marginBottom: "40px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "11px",
                                textTransform: "uppercase",
                                color: "#6b7280",
                                fontWeight: "700",
                                letterSpacing: "0.05em",
                              }}
                            >
                              TRANSCRIPTION
                            </span>
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "800",
                                color: "#111827",
                              }}
                            >
                              {"0.1"}
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "4px",
                                backgroundColor: "#f3f4f6",
                                padding: "6px",
                                borderRadius: "6px",
                              }}
                            >
                              <div style={{ flex: 1, textAlign: "center" }}>
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#6b7280",
                                    fontWeight: "600",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Corrected Sentence
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "800",
                                    color: "#111827",
                                  }}
                                >
                                  {version.lines_changed ?? "-"}
                                </div>
                              </div>
                              <div
                                style={{
                                  width: "1px",
                                  backgroundColor: "#d1d5db",
                                }}
                              ></div>
                              <div style={{ flex: 1, textAlign: "center" }}>
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#6b7280",
                                    fontWeight: "600",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Words Added
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "800",
                                    color: "#111827",
                                  }}
                                >
                                  {version.total_words_added ?? "-"}
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "4px",
                                backgroundColor: "#f3f4f6",
                                padding: "6px",
                                borderRadius: "6px",
                              }}
                            >
                              <div style={{ flex: 1, textAlign: "center" }}>
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#6b7280",
                                    fontWeight: "600",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Agent Changes
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "800",
                                    color: "#111827",
                                  }}
                                >
                                  {(() => {
                                    const agentChanges =
                                      version.changes?.filter((change) => {
                                        const speechItem =
                                          callData?.speech?.[change.index];
                                        return speechItem?.speaker === "agent";
                                      }).length ?? 0;
                                    return agentChanges;
                                  })()}
                                </div>
                              </div>
                              <div
                                style={{
                                  width: "1px",
                                  backgroundColor: "#d1d5db",
                                }}
                              ></div>
                              <div style={{ flex: 1, textAlign: "center" }}>
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#6b7280",
                                    fontWeight: "600",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Customer Changes
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "800",
                                    color: "#111827",
                                  }}
                                >
                                  {(() => {
                                    const customerChanges =
                                      version.changes?.filter((change) => {
                                        const speechItem =
                                          callData?.speech?.[change.index];
                                        return (
                                          speechItem?.speaker === "customer"
                                        );
                                      }).length ?? 0;
                                    return customerChanges;
                                  })()}
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#fffff",
                                textAlign: "center",
                                backgroundColor: "#f9fafb",
                                padding: "4px 6px",
                                borderRadius: "4px",
                                fontWeight: "600",
                              }}
                            >
                              <span style={{ fontWeight: "600" }}>
                                Edited:{" "}
                              </span>
                              {formatDateTime(version.edited_at)}
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#2563eb",
                                textAlign: "center",
                                fontWeight: "600",
                              }}
                            >
                              By: {version.edited_by}
                            </div>
                          </div>
                        )}
                      </div>

                      <div
                        className=""
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          paddingBottom: "10px",
                        }}
                      >
                        <div className="transcription-box">
                          <div
                            className="transcription-scrollable"
                            style={{ height: "500px" }}
                          >
                            <div className="transcription-info-wrapper clearfix">
                              {callData?.speech?.map((item, index) => {
                                return (
                                  <div
                                    key={index}
                                    className="transcription-customer-section clearfix"
                                    style={{
                                      backgroundColor: "transparent",
                                    }}
                                  >
                                    <h4>
                                      {item.speaker_name?.split(" ")[0] ||
                                        "Speaker"}
                                    </h4>
                                    <div className="customer-info-right">
                                      <span>{formatTime(item.startTime)}</span>
                                      <p>
                                        {version.isOriginal
                                          ? displayEnglish
                                            ? item.dialogue ||
                                              item.early_dialogue
                                            : item.early_dialogue ||
                                              item.dialogue
                                          : renderDiff(index, version)}
                                      </p>
                                      <span>{formatTime(item.endTime)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {allVersions.length > 1 && (
                    <div
                      style={{
                        minWidth: "580px",
                        flex: "0 0 580px",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                        borderRadius: "12px",

                        boxShadow: "0 4px 12px rgba(39,16,120,0.15)",
                        overflow: "hidden",
                        // height: "calc(100vh - 260px)",
                      }}
                    >
                      <div
                        style={{
                          padding: "1rem",
                          // borderBottom: "2px solid #271078",
                          flexShrink: 0,
                          backgroundColor: "#f9fafb",
                          marginBottom: "56px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "800",
                            fontSize: "16px",
                            marginBottom: "8px",
                            color: "#271078",
                            textAlign: "center",
                          }}
                        >
                          Revision Trail
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6b7280",
                            textAlign: "center",
                            fontWeight: "600",
                          }}
                        >
                          Final version with all accumulated changes
                        </div>
                      </div>

                      <div
                        className="transcription-white-global"
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          paddingBottom: "10px",
                        }}
                      >
                        <div className="transcription-box">
                          <div
                            className="transcription-scrollable"
                            style={{ height: "500px" }}
                          >
                            <div className="transcription-info-wrapper clearfix">
                              {callData?.speech?.map((item, index) => {
                                const hasAnyChange = allVersions.some(
                                  (v) =>
                                    !v.isOriginal &&
                                    v.changes?.some((c) => c.index === index),
                                );

                                return (
                                  <div
                                    key={index}
                                    className="transcription-customer-section clearfix"
                                  >
                                    <h4>
                                      {item.speaker_name?.split(" ")[0] ||
                                        "Speaker"}
                                    </h4>
                                    <div className="customer-info-right">
                                      <span>{formatTime(item.startTime)}</span>
                                      <p>
                                        {hasAnyChange
                                          ? renderAuditTrailDiff(index)
                                          : displayEnglish
                                            ? item.dialogue ||
                                              item.early_dialogue
                                            : item.early_dialogue ||
                                              item.dialogue}
                                      </p>
                                      <span>{formatTime(item.endTime)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inline Comments Section */}
              <div
                id="comments-section"
                style={{
                  backgroundColor: "white",
                  borderRadius: "0.5rem",
                  border: "1px solid #DFDFDF",
                }}
                className="px-8 py-12 mx-auto my-8 "
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "2rem",
                  }}
                >
                  <div
                    className="flex items-center gap-2"
                    style={{ marginLeft: "-5px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#271078"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>

                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#271078",
                        marginBottom: "0.25rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      Comments
                    </h4>
                    {(notesData?.notes?.length ?? 0) > 0 && (
                      <span
                        style={{
                          backgroundColor: "#271078",
                          color: "white",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          fontWeight: "700",
                          marginLeft: "8px",
                        }}
                      >
                        {notesData.notes.length}
                      </span>
                    )}
                  </div>
                  <div>
                    <button
                      className="notes-add-btn"
                      onClick={() => {
                        setNotesInput("");
                        setShowNotesModal(true);
                      }}
                      style={{ 
                        padding: "8px 16px",
                        backgroundColor: "white",
                        color: "#271078",
                        border: "2px solid #271078",
                        borderRadius: "8px",
                        fontWeight: "700",
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      + Add Comment
                    </button>
                  </div>
                </div>

                <div style={{ flex: 1 }}>
                  {(notesData?.notes?.length ?? 0) === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px 0",
                        color: "#9ca3af",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#DFDFDF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginBottom: "12px" }}
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                      <p style={{ fontSize: "14px", textAlign: "center" }}>
                        No comments added yet.
                        <br />
                        Click &ldquo;+ Add Comment&rdquo; to get started.
                      </p>
                    </div>
                  ) : (
                    notesData.notes.map((note) => (
                      <div
                        key={note.id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          padding: "16px",
                          marginBottom: "12px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          border: "1px solid #DFDFDF",
                          boxShadow: "0 1px 4px rgba(39,16,120,0.06)",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "15px",
                              color: "#171717",
                              lineHeight: "1.6",
                              wordBreak: "break-word",
                            }}
                          >
                            {note.text}
                          </p>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#9ca3af",
                              marginTop: "8px",
                              display: "block",
                              fontWeight: "500",
                            }}
                          >
                            {note.date}
                          </span>
                        </div>
                        <button
                          onClick={() => setNoteToDelete(note.id)}
                          title="Delete note"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#c2c2c2",
                            marginLeft: "16px",
                            padding: "4px",
                            flexShrink: 0,
                            transition: "color 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#ef4444")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#c2c2c2")
                          }
                        >
                          <i
                            className="fa fa-trash"
                            aria-hidden="true"
                            style={{ fontSize: "16px" }}
                          />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Delete Confirmation Modal */}
      {noteToDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "24px",
              borderRadius: "12px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                marginTop: 0,
                fontSize: "18px",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              Delete Comment
            </h3>
            <p style={{ color: "#4b5563", fontSize: "14px", marginTop: "8px" }}>
              Are you sure you want to delete this comment? This action cannot
              be undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "24px",
              }}
            >
              <button
                onClick={() => setNoteToDelete(null)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: "white",
                  color: "#374151",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteNote}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#dc2626",
                  color: "white",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Delete Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNotesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9990,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowNotesModal(false)}
        >
          <div
            style={{
              background: "#FDFCFF",
              borderRadius: "12px",
              padding: "0",
              width: "520px",
              maxWidth: "94vw",
              maxHeight: "80vh",
              boxShadow: "0 12px 40px rgba(39,16,120,0.18)",
              display: "flex",
              flexDirection: "column",
              border: "1px solid #DFDFDF",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px",
                borderBottom: "1px solid #DFDFDF",
                background: "white",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#271078",
                  }}
                >
                  Add Comment
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <button
                  onClick={() => setShowNotesModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "22px",
                    cursor: "pointer",
                    color: "#6b7280",
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Body: add-note form */}
            <div style={{ padding: "20px 24px" }}>
              <p
                style={{
                  fontSize: "13px",
                  color: "#484848",
                  marginBottom: "10px",
                  fontWeight: "600",
                }}
              >
                Write your comment
              </p>
              <textarea
                rows={5}
                placeholder="Write your comment here..."
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #C2C2C2",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "14px",
                  color: "#171717",
                  resize: "vertical",
                  outline: "none",
                  boxSizing: "border-box",
                  background: "white",
                  fontFamily: "inherit",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "14px",
                }}
              >
                <button
                  onClick={() => setShowNotesModal(false)}
                  style={{
                    backgroundColor: "white",
                    color: "#484848",
                    border: "1px solid #C2C2C2",
                    borderRadius: "8px",
                    padding: "7px 18px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddNote}
                  style={{
                    backgroundColor: "#271078",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "7px 18px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 6px -1px rgba(39, 16, 120, 0.4)",
                  }}
                >
                  Save Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default TranscriptionAudit;
